import { useEffect, useState } from 'react';
import { useWakeLock } from 'react-screen-wake-lock';
import { Api } from 'lavva.exalushome';
import { Status } from 'lavva.exalushome/build/js/DataFrame';
import { ResponseResult } from 'lavva.exalushome/build/js/Services/FieldChangeResult';
import {
  ControllerUpdatesInfo,
  ControllerUpdateType,
  IControllerAvailableUpdateInfo,
} from 'lavva.exalushome/build/js/Services/Updates/IUpdateInfo';
import { ControllerVersion } from 'lavva.exalushome/build/js/Services/Updates/IUpdateInfo';
import {
  DownloadProgressInfo,
  IUpdatesService,
  UpdateErrorCode,
  UpdateProgressInfo,
} from 'lavva.exalushome/build/js/Services/Updates/IUpdatesService';
import { UpdatesService } from 'lavva.exalushome/build/js/Services/Updates/UpdatesService';
import { useBackdropContext } from '../../../../../context/backdrop';
import { ROUTES } from '../../../../../routes/routes';
import { useUpdateErrors } from '../../../hooks/common/use-update-errors';
import { useHandleDataFrameErrors } from '../../../hooks/errors';
import { ProcessKey, UpdateProcess } from '../types';
import { useUpdateProcess } from './use-update-process';

export const useControllerUpdates = () => {
  const { turnOnBackdrop, turnOffBackdrop, turnOffBackdropPopup } = useBackdropContext();
  const [retransmitterPopup, setRetransmitterPopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [controllerUpdate, setControllerUpdate] = useState<ControllerUpdatesInfo | null>(null);
  const [controllerVersion, setControllerVersion] = useState<ControllerVersion[]>([]);
  const [versionPopup, setVersionPopup] = useState<boolean>(false);
  const [updateFailedPopup, setUpdateFailedPopup] = useState<boolean>(false);
  const [canCancelOrGetPendingUpdate, setCanCancelOrGetPendingUpdate] = useState<boolean>(false);
  const { handleUpdatesResponse, showUpdateError } = useUpdateErrors();
  const { request, release } = useWakeLock();
  const { handleError } = useHandleDataFrameErrors();

  const checkIfCanCancel = async () => {
    const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
    const result = await updateService.CanCancelFailedControllerUpdateOrGetPendingUpdateProgress();
    setCanCancelOrGetPendingUpdate(result);
  };

  useEffect(() => {
    request();

    return () => {
      release();
    };
  }, []);

  const getCurrentControllerVersion = async () => {
    turnOnBackdrop();
    const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
    const result = await updateService.CheckControllerVersionAsync();

    handleUpdatesResponse<ControllerVersion[]>(result, () => {
      setControllerVersion(result as ControllerVersion[]);
      setVersionPopup(true);
      turnOffBackdrop();
    });
  };

  const setManualAction = () => {
    setLoading(true);
    localStorage.setItem('manualAction', 'true');
  };

  const clearManualAction = () => localStorage.removeItem('manualAction');

  const finishDownloadOrUpdate = (
    result: Status.OK | ResponseResult<UpdateErrorCode>,
    updateProcess: UpdateProcess,
    allUpdates: boolean,
  ) => {
    setLoading(false);

    if (result === Status.OK) {
      if (updateProcess === UpdateProcess.Download) {
        checkForControllerUpdates();
        turnOffBackdropPopup();
      } else {
        redirectToHome();
      }
    } else {
      clearManualAction();
      showUpdateError(result as ResponseResult<UpdateErrorCode>);

      if (canCancelOrGetPendingUpdate) {
        if (allUpdates) {
          const updates = controllerUpdate?.UpdatesAvailable || [];
          const controllerUpdateExists = !!updates.find((x) => x.UpdateType === ControllerUpdateType.SoftwareUpdate);
          if (updates.length === 1 && controllerUpdateExists) setUpdateFailedPopup(true);
        } else {
          setUpdateFailedPopup(true);
        }
      }
    }
  };

  const redirectToHome = () => {
    clearManualAction();
    window.location.href = ROUTES.Root();
  };

  const checkForControllerUpdates = async () => {
    setControllerUpdate(null);

    turnOnBackdrop();
    const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
    const result = await updateService.CheckControllerUpdatesAsync();
    clearManualAction();

    if (result.UpdatesAvailable.length > 0 || result.UpdatesNotAvailable.length > 0) {
      setControllerUpdate(result);
    }

    turnOffBackdrop();
  };

  const { onUpdateControllerProgress, onUpdateAllControllerProgresses, onDownloadControllerProgress } =
    useUpdateProcess({
      checkForControllerUpdates,
    });

  const checkControllerPendingUpdateProgressAsync = async (
    update: IControllerAvailableUpdateInfo,
    onlyDownload: boolean,
  ) => {
    const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);

    const result = await updateService.CheckControllerPendingUpdateProgressAsync(
      (updateProgress: UpdateProgressInfo) => {
        const manualAction = localStorage.getItem('manualAction');

        if (!onlyDownload && !manualAction) {
          setLoading(true);
          onUpdateControllerProgress(updateProgress, update);
        }
      },
      (downloadProgress: DownloadProgressInfo) => {
        const manualAction = localStorage.getItem('manualAction');

        if (!manualAction) {
          setLoading(true);
          onDownloadControllerProgress(downloadProgress);
        }
      },
    );

    const manualAction = localStorage.getItem('manualAction');

    if (manualAction) return;

    setLoading(false);
    if (result === Status.OK) {
      if (onlyDownload) {
        if (update.UpdateIsDownloading) checkForControllerUpdates();
        turnOffBackdropPopup();
      } else {
        redirectToHome();
      }
    } else {
      localStorage.removeItem('manualAction');
      showUpdateError(result as ResponseResult<UpdateErrorCode>);

      if (canCancelOrGetPendingUpdate) setUpdateFailedPopup(true);
    }
  };

  const cancelControllerUpdateAndTryAgain = async () => {
    if (!canCancelOrGetPendingUpdate) return;
    turnOnBackdrop();

    const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);
    const result = await updateService.CancelFailedControllerUpdateAsync();

    if (result === Status.OK) {
      setUpdateFailedPopup(false);
      checkForControllerUpdates();
    } else {
      handleError(result);
    }
  };

  const downloadControllerUpdate = async () => {
    setManualAction();

    setTimeout(async () => {
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);

      const result = await updateService.DownloadControllerUpdateAsync((downloadProgress: DownloadProgressInfo) => {
        onDownloadControllerProgress(downloadProgress);
      });

      finishDownloadOrUpdate(result, UpdateProcess.Download, false);
    }, 800);
  };

  const installControllerUpdate = async (update: IControllerAvailableUpdateInfo) => {
    setManualAction();

    setTimeout(async () => {
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);

      const result = await updateService.InstallControllerOrRadioUpdateAsync(
        (updateProgress: UpdateProgressInfo) => {
          onUpdateControllerProgress(updateProgress, update);
        },
        (downloadProgress: DownloadProgressInfo) => {
          onDownloadControllerProgress(downloadProgress);
        },
        update.UpdateIdentifier,
      );

      finishDownloadOrUpdate(result, UpdateProcess.Update, false);
    }, 800);
  };

  const installAllUpdates = async () => {
    const updates = controllerUpdate?.UpdatesAvailable || [];
    if (!updates.length) return;
    setManualAction();

    setTimeout(async () => {
      const updateService = Api.Get<IUpdatesService>(UpdatesService.ServiceName);

      const result = await updateService.InstallControllerOrRadioUpdateAsync(
        (updateProgress: UpdateProgressInfo) => {
          onUpdateAllControllerProgresses(updateProgress, updates);
        },
        (downloadProgress: DownloadProgressInfo) => {
          onDownloadControllerProgress(downloadProgress);
        },
      );

      finishDownloadOrUpdate(result, UpdateProcess.Update, true);
    }, 800);
  };

  const handleDownloadOrUpdateController = (process: ProcessKey) => {
    if (process === ProcessKey.DownloadStart) {
      downloadControllerUpdate();
    } else if (process === ProcessKey.UpdateStart) {
      installControllerUpdate(controllerUpdate?.UpdatesAvailable[0] as IControllerAvailableUpdateInfo);
    }
  };

  return {
    controllerUpdate,
    retransmitterPopup,
    loading,
    controllerVersion,
    versionPopup,
    updateFailedPopup,
    canCancelOrGetPendingUpdate,
    setUpdateFailedPopup,
    checkForControllerUpdates,
    handleDownloadOrUpdateController,
    installAllUpdates,
    setRetransmitterPopup,
    getCurrentControllerVersion,
    setVersionPopup,
    checkIfCanCancel,
    cancelControllerUpdateAndTryAgain,
    checkControllerPendingUpdateProgressAsync,
  };
};
